import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { catchError, map, filter } from 'rxjs/operators';
import { alert, confirm } from 'devextreme/ui/dialog';
import { EidosApiService } from './eidos-api.service';
import { EidosLogService } from './eidos-log.service';
import { EidosSecurityService } from './eidos-security.service';
import { CoreFormatService } from './core-format.service';
import { CoreRoute, EidosAuthType, EidosLogSeverity, LocalStorageKeys, MenuActionType, SecurityCheckRequestType } from '@eidos/models/core-constant.model';
import { MyBizApiResponse, MyBizLabels, MyBizVersionApiResponse } from '@eidos/models/mybiz.model';
import { EidosMenu } from '@eidos/models/eidos-menu.model';
import { IEidosDashboardConfig } from '@eidos/models/eidos-dashboard.interface';
import { IEidosMenuAction } from './environment.interface';
import { EidosUser } from '@eidos/models/user-info.model';
import { EidosDataObjectFieldSort, EidosDataObjectPagination, EidosDataRequest, EidosObjectApiResponse, EidosObjectDataApiResponse, EidosSortType } from '@eidos/models/eidos-object.model';
import { IEidosDataRequestOptions } from '@eidos/models/eidos-object.interface';
import { IEidosResetCredentialsRequest } from '@eidos/models/mybiz.interface';
import { EidosLanguage } from '@eidos/models/eidos-language.model';

export enum EidosUtilityDialogDialogType {
  Generic,
  Warning,
  Info,
  Debug,
  Alert,
  Fault,
  Error,
  Ask
}
export interface IEidosUtilityDialogOptions {
  title?: string;
  dialogType?: EidosUtilityDialogDialogType;
  message: string;
  info?: string;
  iconSize?: number;
  error?: Array<string>;
  size?: string;
}
export class EidosUtilityDialog {
  constructor() { }

  getTemplateIcon(dialogType: EidosUtilityDialogDialogType, iconSize: number): string {
    const size = iconSize > 0 && iconSize < 5 ? iconSize : 3;
    switch (dialogType) {
      case EidosUtilityDialogDialogType.Debug:
        return `<i class="fas fa-bug fa-${size}x icon-debug"></i>`;
      case EidosUtilityDialogDialogType.Alert:
        return `<i class="fas fa-radiation fa-${size}x icon-alert"></i>`;
      case EidosUtilityDialogDialogType.Warning:
        return `<i class="fas fa-exclamation-triangle fa-${size}x icon-warning"></i>`;
      case EidosUtilityDialogDialogType.Info:
        return `<i class="fas fa-info-circle fa-${size}x icon-info"></i>`;
      case EidosUtilityDialogDialogType.Fault:
        return `<i class="fas fa-skull-crossbones fa-${size}x icon-fault"></i>`;
      case EidosUtilityDialogDialogType.Error:
        return `<i class="fas fa-times-circle fa-${size}x icon-error"></i>`;
      case EidosUtilityDialogDialogType.Ask:
        return `<i class="fas fa-question-circle fa-${size}x icon-ask"></i>`;
      //Generic
      default:
        return `<i class="fas fa-info fa-${size}x icon-generic"></i>`;
    }
  }
  getMessageConfirmTemplate(data: IEidosUtilityDialogOptions): string {
    const d = Object.assign(data, { dialogType: data.dialogType ?? EidosUtilityDialogDialogType.Ask });
    return this.getMessageTemplate(d);
  }
  getMessageErrorTemplate(data: IEidosUtilityDialogOptions): string {
    const d = Object.assign(data, { dialogType: data.dialogType ?? EidosUtilityDialogDialogType.Error });
    return this.getMessageTemplate(d, data.error ?? []);
  }
  getMessageAlertTemplate(data: IEidosUtilityDialogOptions): string {
    const d = Object.assign(data, { dialogType: data.dialogType ?? EidosUtilityDialogDialogType.Alert });
    return this.getMessageTemplate(d);
  }
  getMessageTemplate(data: IEidosUtilityDialogOptions, extraInfo: Array<string> = []): string {
    const icon = this.getTemplateIcon(data.dialogType ?? EidosUtilityDialogDialogType.Generic, data.iconSize ?? 3);
    return `
    <style>
      .dx-popup-normal div.eidos-dialog {
        width:${data.size ?? 'auto'};
        font-size:1.2rem;
        max-height: 70vh;
        display: flex;
        flex-direction: column;
      }
      .dx-popup-normal .dx-button-content {
        font-size:1.2rem;
        font-weight: 900;
        border: 1px solid black;
        border-radius: 0.4rem;
      }
      .dx-popup-title {
        font-weight: 900;
        background-color: var(--primary-background-color);
        color: var(--primary-color);
      }
      .eidos-dialog-msg {
        padding: 1rem;
      }
    </style>
    <div class="eidos-dialog">
      ${icon}

    <dx-scroll-view class="flex-grow-1 overflow-auto" [scrollByThumb]="true" [scrollByContent]="true" showScrollbar="always">
    <p class="eidos-dialog-msg">
        ${data.message}
      </p>

      <br />

      <p class="eidos-dialog-info">
        ${data.info ?? ''}
      </p>
      <br />

      <p class="eidos-dialog-extra-info">
        <ul>
          ${extraInfo.map(ei => `<li>${ei}</li>`).join("")}
        </ul>
      </p></dx-scroll-view>


    </div>
    `;
  }
  public error(data: IEidosUtilityDialogOptions): void {
    alert(this.getMessageErrorTemplate(data), data.title ?? '');
  }
  public alert(data: IEidosUtilityDialogOptions): void {
    alert(this.getMessageAlertTemplate(data), data.title ?? '');
  }
  public confirm(data: IEidosUtilityDialogOptions): Promise<boolean> {
    return confirm(this.getMessageConfirmTemplate(data), data.title ?? '');
  }
}

@Injectable({
  providedIn: 'root'
})
export class EidosUtilityService {
  /**
   * Only admin actions
   *
   * @readonly
   * @private
   * @memberof EidosUtilityService
   */
  private readonly ADMIN_ACTIONS = [MenuActionType.Debug, MenuActionType.Readme];

  /**
   * Eidos labels from MyBiz
   *
   * @private
   * @type {MyBizLabels}
   * @memberof EidosUtilityService
   */
  private labels: MyBizLabels = new MyBizLabels();

  /**
   * Current debug mode
   *
   * @memberof EidosUtilityService
   */
  public inDebugMode = new BehaviorSubject<boolean>(false);

  /**
   * Current menu open/closed status
   *
   * @memberof EidosUtilityService
   */
  public isMenuClosed = new BehaviorSubject<boolean>(true);

  /**
   * Current menu pin/unpin status
   *
   * @memberof EidosUtilityService
   */
  public isMenuPinned = new BehaviorSubject<boolean>(false);
  /**
   * Hide sidebar flag
   *
   * @type {BehaviorSubject<boolean>}
   * @memberof EidosUtilityService
   */
  public hideSidebar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /**
   * Hide topbar flag
   *
   * @type {BehaviorSubject<boolean>}
   * @memberof EidosUtilityService
   */
  public hideTopbar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Current menu
   *
   * @memberof EidosUtilityService
   */
  public currentMenu: BehaviorSubject<EidosMenu>;

  /**
   * Current dashboard
   *
   * @memberof EidosUtilityService
   */
  public currentDashboard: BehaviorSubject<IEidosDashboardConfig | undefined>;

  /**
   * Current user actions
   *
   * @memberof EidosUtilityService
   */
  public currentActions: BehaviorSubject<Array<IEidosMenuAction>>;

  /**
   * Current user info
   *
   * @private
   * @type {(EidosUser | null)}
   * @memberof EidosUtilityService
   */
  private currentUser: EidosUser | null = null;

  /**
   * Default actions in profile menu
   *
   * @private
   * @type {Array<IEidosMenuAction>}
   * @memberof EidosUtilityService
   */
  private defaultMenuActions: Array<IEidosMenuAction> = [
    {
      id: MenuActionType.Readme,
      icon: 'perm_data_setting',
      label:'',
      labelCod: 'readme',
      enabled: true,
      order: 100
    },
    {
      id: MenuActionType.Debug,
      icon: 'adb',
      label:'',
      labelCod: 'debug_mode',
      enabled: true,
      order: 100
    },
    {
      id: MenuActionType.Settings,
      icon: 'settings',
      label:'',
      labelCod: 'settings',
      enabled: true,
      order: 50
    },
    {
      id: MenuActionType.ChangePassword,
      icon: 'lock',
      label:'',
      labelCod: 'change_password',
      enabled: true,
      order: 50
    },
    {
      id: MenuActionType.Refresh,
      icon: 'cached',
      label:'',
      labelCod: 'cache',
      enabled: true,
      order: 10
    },
    {
      id: MenuActionType.Impersonate,
      icon: 'people',
      label:'',
      labelCod: 'impersonate_user',
      enabled: false,
      order: 100
    },
    {
      id: MenuActionType.StopImpersonate,
      icon: 'person',
      label:'',
      labelCod: 'user_impersonated',
      enabled: false,
      order: 100
    },
    {
      id: MenuActionType.Logout,
      icon: 'power_settings_new',
      label:'',
      labelCod: 'logout',
      enabled: true,
      order: -100
    }
  ];

  constructor(
    private eidosApi: EidosApiService
    , private router: Router
    , private eidosSecurityService: EidosSecurityService
    , private eidosLogService: EidosLogService) {

    this.currentMenu = new BehaviorSubject<EidosMenu>(new EidosMenu());
    this.currentDashboard = new BehaviorSubject<IEidosDashboardConfig | undefined>(undefined);
    this.currentActions = new BehaviorSubject<Array<IEidosMenuAction>>([]);

    this.eidosSecurityService.currentLoggedUser
      .subscribe(user => {
        // Current user can be invalid
        this.currentUser = user;
        this.setupUtilities(user);
      });

    this.eidosSecurityService.authenticated
      .pipe(filter(isAuthenticated => isAuthenticated !== undefined))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated && this.router.url !== CoreRoute.Login) {
          sessionStorage.clear();
          window.location.href = window.location.origin + '/' + CoreRoute.Login;
          // this.router.navigate([CoreRoute.Login]);
        }
      });
  }
  /**
   * Setup utilities of service
   *
   * @private
   * @param {EidosUser} user
   * @memberof EidosUtilityService
   */
  private setupUtilities(user: EidosUser) {
    // If valid, setup utilities
    if (user.isValid) {

      this.setDebugMode(localStorage.getItem(LocalStorageKeys.DEBUG_MODE) === "true");

      this.setConfigActions();

      this.loadLabels();

      this.currentDashboard.next(user.dashboard);

      this.eidosApi.loadMenu().subscribe({
        next: (menu: EidosMenu) => {
          // this._currentMenu = menu;
          this.currentMenu.next(menu);
          this.setupMenuClosing(user);
        },
        error: (error) => {
          this.eidosLogService.logDebug(EidosLogSeverity.Error, "EidosUtilityService::setupUtilities - eidosApi.loadMenu", error);
        },
        complete: () => { }
      });

    }
    else {
      // If user is invalid, set default utilities
      this.labels = new MyBizLabels();
      this.currentActions.next([]);
      this.currentDashboard.next({
        enabled: false,
        columns: 10,
        rows: 10,
        columnsGap: 0,
        rowsGap: 0,
        eidosObject: []
      });
      this.currentMenu.next(new EidosMenu());
      if (localStorage.getItem(LocalStorageKeys.DEBUG_MODE) === null) {
        this.setDebugMode(false);
      }
      this.setupMenuClosing(user);
    }
  }

  /**
   * Load labels from APIs
   *
   * @memberof EidosUtilityService
   */
  public loadLabels(): void {
    this.eidosApi.getMyBizLabels().subscribe({
      next: (labels: MyBizLabels) => {
        this.labels = labels;
      },
      error: (error) => {
        this.labels = new MyBizLabels();
        this.eidosLogService.logDebug(EidosLogSeverity.Error, "EidosUtilityService::setupUtilities - eidosApi.loadLabels", error);
      },
      complete: () => { }
    });
  }

  /**
   * Setup the menu closing after its loading.
   * Set the menu as closed and unpinned if user is invalid
   *
   * @private
   * @param {EidosUser} user
   * @memberof EidosUtilityService
   */
  private setupMenuClosing(user: EidosUser): void {
    if (user.isValid) {
      let pinMenu = user.menuPinned;
      const cachedLocked = localStorage.getItem(LocalStorageKeys.PINNED_MENU);
      if (cachedLocked) {
        pinMenu = cachedLocked === "true";
      }
      this.setMenuStatus(!pinMenu);
      this.setMenuPin(pinMenu);
    } else {
      this.setMenuPin(false);
      localStorage.removeItem(LocalStorageKeys.PINNED_MENU);
    }
  }

  /**
   * Filters and sets avalaible user actions basing on its permissions
   *
   * @private
   * @memberof EidosUtilityService
   */
  private setConfigActions(): void {

    let userActions = this.defaultMenuActions
      // If user authentication type is not Form, discard change password action
      .filter(action => this.currentUser?.authType === EidosAuthType.Form || (action.id !== MenuActionType.ChangePassword))
      // If user can not impersonate or is not impersonated, discard impersonate actions
      .filter(action => !!this.currentUser?.canImpersonate || !!this.currentUser?.isImpersonated || (action.id !== MenuActionType.Impersonate && action.id !== MenuActionType.StopImpersonate))
      // If user is not admin, discard only admin actions
      .filter(action => !!this.currentUser?.isAdmin || this.ADMIN_ACTIONS.every(actionId => actionId !== action.id));

    userActions = userActions.map(action => {

      switch (action.id) {
        case MenuActionType.Impersonate:
          action.enabled = !this.currentUser?.isImpersonated;
          break;
        case MenuActionType.StopImpersonate:
          action.enabled = !!this.currentUser?.isImpersonated;
          break;
        default:
          break;
      }
      return action;
    })

    this.currentActions.next(userActions);
  }

  /**
   * Retrive label specified by key, or its default
   *
   * @param {string} key
   * @param {string} [defaultText]
   * @param {...Array<string>} placeholders
   * @return {*}  {string}
   * @memberof EidosUtilityService
   */
  public getLabel(key: string, defaultText?: string, ...placeholders: Array<string>): string {

    let result: string;
    defaultText = defaultText ?? '*default';

    if (key) {
      result = this.labels.get(key) ?? defaultText;

      if (result && placeholders && placeholders.length > 0) {
        placeholders.forEach((item, i) => {
          result = result.replace(`${(i + 1)}`, item);
        });
      }
    } else {
      result = defaultText;
    }

    return result;
  }

  /**
   * Gets MyBiz app version
   *
   * @return {*}  {Observable<EidosMyBizVersionApiResponse>}
   * @memberof EidosUtilityService
   */
  getMyBizVersion(): Observable<MyBizVersionApiResponse> {
    return this.eidosApi.getMyBizVersion();
  }
  /**
   * Gets a generic MyBiz object
   *
   * @param {number} objectId
   * @return {*}  {Observable<EidosObjectApiResponse>}
   * @memberof EidosUtilityService
   */
  getObject(objectId: number): Observable<EidosObjectApiResponse> {
    return this.eidosApi.getObject(objectId);
  }

  /**
   * Gets data of a generic MyBiz object
   *
   * @param {number} objectId
   * @param {number} page
   * @param {IEidosDataRequestOptions} [options]
   * @return {*}  {Observable<EidosObjectDataApiResponse>}
   * @memberof EidosUtilityService
   */
  getDataObject(objectId: number, page: number, options?: IEidosDataRequestOptions): Observable<EidosObjectDataApiResponse> {

    let requestOptions: EidosDataRequest = new EidosDataRequest();
    requestOptions.page = page;
    if (options) {
      if (options.pagination) {
        requestOptions.pageSize = (options?.pagination as EidosDataObjectPagination).pageSize
      }
      if (options.sort && options.sort.length > 0) {
        requestOptions.orderby = options.sort!.map((s: EidosDataObjectFieldSort) => {
          return {
            fieldName: s.fieldName,
            priority: s.priority,
            isAscending: s.type === EidosSortType.Ascending
          }
        });
      }
      if (options.filters && options.filters.length > 0) {
        requestOptions.filters = options.filters!;
      }
    }
    this.eidosLogService.logDebug(EidosLogSeverity.Log, 'getDataObject', objectId, requestOptions);
    return this.eidosApi.getDataObject(objectId, requestOptions);
  }

  /**
   * Set or toggle debug mode, if user is admin
   *
   * @param {boolean} [enable] Mode setting
   * @memberof EidosUtilityService
   */
  public setDebugMode(enable?: boolean): void {
    const nextState = enable !== undefined ? enable : (!!this.currentUser?.isAdmin && !this.inDebugMode.value);
    const storageState = nextState ? "true" : "false";
    localStorage.setItem(LocalStorageKeys.DEBUG_MODE, storageState);
    this.eidosLogService.inDebugMode = nextState;
    this.inDebugMode.next(nextState);
  }

  /**
   * Toggle the menu status
   *
   * @param {boolean} [closed]
   * @memberof EidosUtilityService
   */
  public toggleMenuStatus(closed?: boolean): void {
    const nextState = closed !== undefined ? closed : !this.isMenuClosed.value;
    this.setMenuStatus(nextState);
  }

  /**
   * Toggle the menu status, if not pinned
   *
   * @param {boolean} [closed]
   * @memberof EidosUtilityService
   */
  public toggleMenuStatusIfNotPinned(closed?: boolean): void {
    if (!this.isMenuPinned.value) {
      this.toggleMenuStatus(closed);
    }
  }

  /**
   * Change the menu state
   *
   * @param {boolean} close Next menu state
   * @memberof EidosUtilityService
   */
  private setMenuStatus(close: boolean): void {
    this.isMenuClosed.next(close);
  }

  /**
   * Pin/Unpin the menu
   * @param {boolean} pin
   * @memberof EidosUtilityService
   */
  public setMenuPin(pin: boolean): void {
    this.isMenuPinned.next(pin);
    const storageState = pin ? "true" : "false";
    localStorage.setItem(LocalStorageKeys.PINNED_MENU, storageState);
    // Force the menu to close while unpinning it
    if (pin === false) {
      this.setMenuStatus(true);
    }
  }

  /**
   * Logout user from Eidos and redirect him to login
   *
   * @memberof EidosUtilityService
   */
  public logout(): void {
    this.eidosApi.logout()
      .subscribe({
        next: (response) => {
          if (response.isOk()) {
            this.eidosSecurityService.clearAuthentication();
            this.eidosSecurityService.clearRequestedRoute();
            sessionStorage.clear();
            window.location.href = window.location.origin + '/' + CoreRoute.Login;
            // this.router.navigate([CoreRoute.Login])
            return true;
          } else {
            return false;
          }
        },
        error: () => false,
        complete: () => { },
      });
  }

  public getMyBizImageUrl(imageCod: string): Observable<string> {
    return this.eidosApi.getMyBizImageUrl(imageCod);
  }

  public changePassword(oldPassword: string, newPassword: string): Observable<string> {
    const response = this.eidosApi
      .changePassword(oldPassword, newPassword)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          if (response.isOk()) {
            if (this.currentUser?.mustChangePassword) {
              this.updateUser({ must_change_password: false });
            }
            return "";
          } else {
            return response.error ?? "Error while changing password.";
          }
        }),
        catchError(async (err: string) => err)
      );
    return response;
  }

  /**
   * Requests a user credentials reset
   *
   * @param {string} username
   * @return {*}  {Observable<string>}
   * @memberof EidosUtilityService
   */
  public resetCredentials(username: string): Observable<string> {
    const payload: IEidosResetCredentialsRequest = CoreFormatService.IsEmail(username) ? { email: username } : { username: username };

    const response = this.eidosApi
      .resetCredentials(payload)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          return response.isOk() ? response.data : "";
        }),
        catchError(async (err) => "")
      );
    return response;
  }
  /**
   * Confirms a user credentials reset
   *
   * @param {string} resetId
   * @return {*}  {Observable<string>}
   * @memberof EidosUtilityService
   */
  public confirmResetCredentials(resetId: string): Observable<string> {
    const response = this.eidosApi
      .confirmResetCredentials(resetId)
      .pipe(
        map<MyBizApiResponse, string>(response => {
          return response.isOk() ? response.data : "";
        }),
        catchError(async (err) => "")
      );
    return response;
  }

  private updateUser(propertyToUpdate: any) {
    // this.currentUser = this.securityService.UpdateUserProperty(propertyToUpdate);
    this.eidosSecurityService.updateUserProperty(propertyToUpdate);
  }

  public impersonateStart(user: string): Observable<boolean> {
    const response = this.eidosApi
      .impersonate(user)
      .pipe(
        map<MyBizApiResponse, boolean>(response => {
          this.reloadUser();
          return response.isOk();
        }),
        catchError(async (err) => false)
      );
    return response;
  }
  public impersonateEnd(): Observable<boolean> {
    const response = this.eidosApi
      .endImpersonate()
      .pipe(
        map<MyBizApiResponse, boolean>(response => {
          this.reloadUser();
          return response.isOk();
        }),
        catchError(async (err) => {
          this.eidosLogService.logDebug(EidosLogSeverity.Log, err);
          return false;
        })
      );
    return response;
  }
  public getAvailableLanguages(): Observable<Array<EidosLanguage>> {
    return this.eidosApi.getLanguages();
  }
  public getAvailableCompanies(): Observable<Array<EidosLanguage>> {
    return this.eidosApi.getCompanies();
  }


  public setLanguage(code: string): Observable<boolean> {
    const response = this.eidosApi
      .setCurrentLanguage(code)
      .pipe(
        map<MyBizApiResponse, boolean>(response => response.isOk()),
        catchError(async (err) => false)
      );
    return response;
  }
  public setCompany(code: string): Observable<boolean> {
    const response = this.eidosApi
      .setCurrentCompany(code)
      .pipe(
        map<MyBizApiResponse, boolean>(response => response.isOk()),
        catchError(async (err) => false)
      );
    return response;
  }

  updateCurrentCompany(companyCode: string): Observable<boolean> {
    const response = this.setCompany(companyCode).pipe(
      map<boolean, boolean>(company => {
        this.loadLabels();
        this.reloadUser();
        return company;
      }),
      catchError(async () => false)
    );
    return response;
  }



  //TODO modellizare setting
  public updateCurrentSetting(setting: any): Observable<boolean> {
    const response = forkJoin([
      this.setCompany(setting.companyCode),
      this.setLanguage(setting.languageCode)
    ]).pipe(
      map<Array<boolean>, boolean>(([company, language]) => {
        this.loadLabels();
        this.reloadUser();
        return company && language;
      }),
      catchError(async (err) => false)
    );
    return response;
  }
  public getCurrentUser(): EidosUser | null {
    return this.currentUser;
  }

  /**
   * Force reload user
   *
   * @memberof EidosUtilityService
   */
  private reloadUser(): void {
    this.eidosSecurityService.checkRequest.emit(SecurityCheckRequestType.User);
  }
}
